import { useMemo, useState } from 'react';
import { makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, useMediaQuery, useTheme } from '@material-ui/core';
import Avatar from './../Avatar';
import UserCard from '../../UserCard';
import Modal from '../../Modal';
import TeamSelect from '../../TeamSelect';
import LevelScaleHeader, { MAX_NAMED_LEVEL_COLUMNS } from '../../LevelScaleHeader';
import SkillLevelTrack from '../../SkillLevelTrack';
import {
  useGetCurrentUserQuery,
  useDeleteSkillMutation,
  useGetLevelOfSkillsQuery,
  useGetUsersQuery,
  useUpdateSkillMutation,
  usePrefetch,
} from 'utils/wemble-api';
import { CompetenceDto, CompetenceUserDto, LevelOfSkillDto } from 'utils/wemble-api.generated';
import organization from 'utils/organization';
import { compareString } from 'helpers/compareFunctions';

const ALL_TITLES = '__all_titles__';


const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: 'Roboto',
  },
  // The heading and the filters share one band, so the row (not the heading)
  // owns the space above the table — otherwise the filters sat in a gap.
  members__title: {
    fontSize: 24,
    fontWeight: 600,
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
    },
  },
  members__content: {
    backgroundColor: 'white',
    padding: 30,
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      padding: 16,
    },
  },
  members__table: {
    tableLayout: 'fixed',
    '& .MuiTableRow-root': {
      WebkitTapHighlightColor: 'transparent',
      WebkitTouchCallout: 'none' as const, // no iOS long-press menu
      '@media (pointer: coarse)': {
        userSelect: 'none' as const,
      },
    },
    '& .MuiTableCell-root': {
      borderBottomColor: theme.palette.gray.g6,
      padding: '8px 0',
      [theme.breakpoints.down('sm')]: {
        padding: '6px 8px',
      },
    },
    '& .MuiTableCell-head': {
      fontSize: 11,
      textTransform: 'uppercase',
      color: theme.palette.gray.g10,
      padding: '0 0 8px',
    },
    '& .MuiTableCell-body': {
      fontWeight: 500,
    },
    '& .MuiTableBody-root .MuiTableRow-root:nth-last-child(1) > td': {
      border: 'none',
    },
  },
  members__titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 10,
    },
  },
  members__addButton: {
    backgroundColor: 'white',
    color: theme.palette.gray.g10,
    padding: '8px 14px',
    fontWeight: 500,
    fontSize: 14,
    borderRadius: 8,
    cursor: 'pointer',
    userSelect: 'none' as const,
  },
  filters: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  titleSelect: {
    fontSize: 13,
    padding: '0 30px 0 14px',
    borderRadius: 12,
    border: 'none',
    backgroundColor: 'white',
    height: 44,
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23999' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    fontWeight: 500,
    fontFamily: 'inherit',
    color: 'inherit',
  },
  // Room between a long name and the level bars (names ran into them on
  // phones); anything still too long is clipped in its own column rather than
  // breaking mid-word or overlapping the bars.
  nameCell: {
    paddingRight: '10px !important',
    overflow: 'hidden',
  },
  trackCell: {
    paddingLeft: '2px !important',
    paddingRight: '2px !important',
  },
  removeCell: {
    [theme.breakpoints.down('sm')]: {
      paddingLeft: '4px !important',
      paddingRight: '0 !important',
    },
  },
  details: {
    backgroundColor: theme.palette.gray.g3,
    display: 'inline-block',
    height: 36,
    width: 36,
    [theme.breakpoints.down('sm')]: {
      height: 30,
      width: 30,
    },
    borderRadius: 8,
    cursor: 'pointer',
    '& > div': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      width: '100%',
    },
  },
}));

const idOf = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const id = (value as { _id?: string | null })._id;
    return id ?? undefined;
  }
  return undefined;
};

const levelOfUser = (user: CompetenceUserDto) => user.skills?.[0]?.level?.level;

const CompetenceMembers = ({
  reload,
  competence,
  onAddMember,
}: {
  reload: () => void;
  competence: CompetenceDto | null | undefined;
  onAddMember?: () => void;
}) => {
  const members = competence?.users;
  const { data: currentUser } = useGetCurrentUserQuery();
  const { data: levelOfSkills } = useGetLevelOfSkillsQuery();
  const prefetchUser = usePrefetch('getUserById');
  const organizationConfig = organization(currentUser?.company);

  const [sortMethod, setSortMethod] = useState<string>('name');
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [titleFilter, setTitleFilter] = useState<string>(ALL_TITLES);

  // TeamSelect works in office ids; a person's office decides which practice
  // group they fall under. The competence payload doesn't carry offices, so the
  // mapping comes from the user list — fetched only once a group is actually
  // picked, keeping the default "All groups" view free of an extra round trip.
  const selectedOfficeIds = useMemo(() => {
    if (selectedTeams.length === 0 || selectedTeams[0] === '__all__') return null;
    return new Set(selectedTeams);
  }, [selectedTeams]);

  const { data: allUsers } = useGetUsersQuery(undefined, { skip: !selectedOfficeIds });

  const [removeSkill] = useDeleteSkillMutation();
  const [updateSkill] = useUpdateSkillMutation();
  const classes = useStyles();
  const [clickedUserId, setClickedUserId] = useState<string | null | undefined>();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('xs'));

  // One column per configured level, lowest first — level 0 ("Would like to
  // learn") included as the first step of the track. Firms run anywhere from two
  // to ten levels, so the columns divide the available width rather than
  // assuming a fixed count.
  const levels = useMemo<LevelOfSkillDto[]>(
    () => [...(levelOfSkills ?? [])].sort((a, b) => (a.level ?? 0) - (b.level ?? 0)),
    [levelOfSkills],
  );

  const officesByUserId = useMemo(() => {
    const map = new Map<string, string[]>();
    (allUsers ?? []).forEach((user) => {
      if (!user._id) return;
      const ids = [idOf(user.office), idOf(user.secondaryOffice)].filter(Boolean) as string[];
      map.set(user._id, ids);
    });
    return map;
  }, [allUsers]);

  const titles = useMemo(() => {
    const seen = new Set<string>();
    (members ?? []).forEach((user) => {
      if (user.title) seen.add(user.title);
    });
    return Array.from(seen).sort((a, b) => compareString(a, b));
  }, [members]);

  const visibleMembers = useMemo(() => {
    let result = [...(members ?? [])];
    // Hold off filtering until the office mapping has arrived, so picking a
    // group shows the unfiltered list for a beat rather than an empty one.
    if (selectedOfficeIds && allUsers) {
      result = result.filter((user) => {
        const userOffices = officesByUserId.get(user._id ?? '') ?? [];
        return userOffices.some((officeId) => selectedOfficeIds.has(officeId));
      });
    }
    if (titleFilter !== ALL_TITLES) {
      result = result.filter((user) => user.title === titleFilter);
    }
    return result.sort((a, b) =>
      sortMethod === 'skill-level'
        ? (levelOfUser(b) ?? -1) - (levelOfUser(a) ?? -1) || compareString(a.name, b.name)
        : compareString(a.name, b.name),
    );
  }, [members, selectedOfficeIds, allUsers, officesByUserId, titleFilter, sortMethod]);

  // Unchanged from the previous list: the same check decides whether levels are
  // editable at all, so no permission gains or loses ground here.
  const canEditLevel = (user: CompetenceUserDto) =>
    Boolean(
      currentUser?.admin ||
        currentUser?.companyAdmin ||
        currentUser?.administratorPriviliges ||
        (!organizationConfig.disableCompetencesEditSelf && currentUser?._id === user._id),
    );

  function getSkillID(clickedUser: CompetenceUserDto) {
    return competence?.users?.find((user) => user._id === clickedUser._id)?.skills?.[0]?._id;
  }

  function setLevel(user: CompetenceUserDto, level: LevelOfSkillDto | null) {
    const skillID = getSkillID(user);
    if (!skillID) return;
    updateSkill({ id: skillID, skillUpdateParams: { levelOfSkillId: level?._id ?? undefined } });
  }

  async function removeUser(user: CompetenceUserDto) {
    if (!user.skills) return;
    for (const skill of user.skills) {
      if (skill.competence === competence?._id && skill._id) {
        await removeSkill({ id: skill._id });
      }
    }
    reload();
  }

  // Shown when the viewer can remove anyone listed — including just
  // themselves (a non-admin removing their own competency), not only admins.
  const showRemoveColumn =
    !!organizationConfig.competencesEditableByEveryone || visibleMembers.some((member) => canEditLevel(member));
  // Name gets a share of the row, the remove button a fixed slot it always
  // fits in, and the level columns split whatever is left evenly (fixed table
  // layout does that for columns without a width).
  const nameColumnWidth = isPhone ? '46%' : '30%';
  const removeColumnWidth = isPhone ? 36 : 56;
  const compactHeader = isPhone || levels.length > MAX_NAMED_LEVEL_COLUMNS;

  return (
    <article className={classes.root}>
      <div className={classes.members__titleRow}>
        <h2 className={classes.members__title}>People ({visibleMembers.length})</h2>
        <div className={classes.filters}>
          <TeamSelect value={selectedTeams} onChange={setSelectedTeams} />
          {titles.length > 1 && (
            <select
              className={classes.titleSelect}
              value={titleFilter}
              onChange={(event) => setTitleFilter(event.target.value)}
            >
              <option value={ALL_TITLES}>All titles</option>
              {titles.map((title) => (
                <option key={title} value={title}>
                  {title}
                </option>
              ))}
            </select>
          )}
          {onAddMember && (
            <Tooltip title={`Add people to ${competence?.name ?? 'this competency'}`} arrow placement="top">
              <div className={classes.members__addButton} onClick={onAddMember}>
                Add person
              </div>
            </Tooltip>
          )}
        </div>
      </div>
      <div className={classes.members__content}>
        <TableContainer>
          <Table className={classes.members__table}>
            <TableHead>
              <TableRow>
                <TableCell
                  onClick={() => setSortMethod('name')}
                  style={{ width: nameColumnWidth, color: sortMethod === 'name' ? '#4e505d' : '#7E8299', cursor: 'pointer' }}
                >
                  <span>Name</span>
                </TableCell>
                <LevelScaleHeader
                  levels={levels}
                  compact={compactHeader}
                  onClick={() => setSortMethod('skill-level')}
                  color={sortMethod === 'skill-level' ? '#4e505d' : '#7E8299'}
                />
                {showRemoveColumn && <TableCell style={{ width: removeColumnWidth }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleMembers.map((user) => {
                const editable = canEditLevel(user);
                return (
                  <TableRow key={user._id}>
                    <TableCell className={classes.nameCell} onMouseEnter={() => prefetchUser({ id: user._id || '' }, { ifOlderThan: 2 })}>
                      <div
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setClickedUserId(user._id);
                        }}
                      >
                        <Avatar image={user.profilePicture} name={user.name} email={user.title} size={isPhone ? 28 : 36} />
                      </div>
                    </TableCell>
                    <TableCell colSpan={levels.length} className={classes.trackCell}>
                      <SkillLevelTrack
                        level={levelOfUser(user) ?? -1}
                        editable={canEditLevel(user)}
                        onChange={(level) => setLevel(user, level)}
                      />
                    </TableCell>
                    {showRemoveColumn && (
                      <TableCell align={'right'} className={classes.removeCell}>
                        {(organizationConfig.competencesEditableByEveryone || editable) && (
                          <Tooltip title={`Remove from ${competence?.name ?? 'this competency'}`} arrow placement="top">
                            <div className={classes.details}>
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeUser(user);
                                }}
                              >
                                <span style={{ fontSize: isPhone ? 20 : 23 }}>&times;</span>
                              </div>
                            </div>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <Modal visible={typeof clickedUserId == 'string'} close={() => setClickedUserId(undefined)} customWidth={1100}>
        {clickedUserId && <UserCard userId={clickedUserId} />}
      </Modal>
    </article>
  );
};

export default CompetenceMembers;
